import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AuthShell from '../components/AuthShell';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const email = location.state?.email || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!email) navigate('/app/login');
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function handleVerify(e) {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setLoading(true);
    try {
      const data = await api('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ email, code }),
      });
      if (data.user) setUser(data.user);
      toast.success('Email verified!');
      if (data.user?.profile_complete) {
        navigate('/app/discover');
      } else {
        navigate('/app/setup');
      }
    } catch (err) {
      toast.error(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    try {
      await api('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      toast.success('Verification code sent');
      setCooldown(30);
    } catch (err) {
      toast.error(err.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell backTo="/app/login">
      <div className="w-16 h-16 rounded-full bg-[#FFE5DD] flex items-center justify-center mb-6">
        <Mail className="w-8 h-8 text-[#C8553D]" />
      </div>
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
        Verify Your Email
      </h1>
      <p className="text-[#6D6875] mt-2 mb-8">
        We sent a 6-digit code to <span className="font-semibold text-[#2B2D42]">{email}</span>
      </p>

      <form onSubmit={handleVerify} className="space-y-5" data-testid="verify-email-form">
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          data-testid="verify-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="w-full text-center text-3xl font-bold tracking-[0.5em] bg-white border-2 border-[#F4F1DE] rounded-lg py-4 outline-none focus:border-[#E07A5F] text-[#2B2D42]"
        />

        <button
          type="submit"
          disabled={loading || code.length !== 6}
          data-testid="verify-submit-button"
          className="w-full bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold py-4 rounded-full transition-colors flex items-center justify-center gap-2 shadow-lg shadow-[#C8553D]/30"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Email'}
        </button>

        <button
          type="button"
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          data-testid="verify-resend-button"
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#C8553D] hover:text-[#9E3A26] py-3 disabled:text-[#B5B5B5]"
        >
          {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </form>
    </AuthShell>
  );
}
